import React from 'react';
import TrendChart from './TrendChart';
import '../styles/HealthSummaryCard.css';

interface HealthSummaryCardProps {
  onViewBodyOverview?: () => void;
  onRiskInfoOpen?: (riskType: 'ascvd' | 'tumor') => void;
}

const HealthSummaryCardB: React.FC<HealthSummaryCardProps> = ({ onViewBodyOverview, onRiskInfoOpen }) => {
  
  const bloodLipidData = [
    { date: '2024-01', value: 5.2 },
    { date: '2024-02', value: 5.8 },
    { date: '2024-03', value: 6.1 },
    { date: '2024-04', value: 5.9 },
    { date: '2024-05', value: 5.6 },
    { date: '2024-06', value: 5.4 }
  ];

  const lungNoduleData = [
    { date: '2024-01', value: 3.2 },
    { date: '2024-02', value: 3.1 },
    { date: '2024-03', value: 3.3 },
    { date: '2024-04', value: 3.2 },
    { date: '2024-05', value: 3.0 },
    { date: '2024-06', value: 2.9 }
  ];

  const handleViewAllTrends = () => {
    console.log('查看全部指标趋势');
  };

  const handleAppointment = (type: string) => {
    console.log(`预约${type}检查`);
  };

  const openRiskInfoPage = (riskType: 'ascvd' | 'tumor') => {
    if (onRiskInfoOpen) {
      onRiskInfoOpen(riskType);
    }
  };

  return (
    <div className="health-summary-card">
      {/* 卡片头部 */}
      <div className="summary-header">
        <h4>健康概况</h4>
        <span className="update-time-right">2024-06-15</span>
      </div>

      {/* 健康摘要 + 风险评估整合版 */}
      <div className="summary-section">
        <div className="unified-health-overview">
          {/* 摘要文字 */}
          <div className="overview-summary">
            <p>整体健康状况良好，需重点关注血脂水平偏高和肺部小结节变化。建议定期复查，保持健康的生活方式。</p>
          </div>
          
          {/* 风险评估卡片 */}
          <div className="risk-cards-container">
            {/* 心血管风险 - 简化样式，与肿瘤预警一致 */}
            <div className="unified-risk-card attention">
              <div className="risk-card-header">
                <div className="risk-name-with-icon">
                  <div className="risk-title-container">
                    <span 
                      className="risk-name clickable-title"
                      onClick={() => openRiskInfoPage('ascvd')}
                    >
                      ASCVD(心血管)风险<button 
                        className="risk-explanation-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          openRiskInfoPage('ascvd');
                        }}
                      >ⓘ</button>
                    </span>
                  </div>
                </div>
                <div className="risk-level-indicator attention">
                  <span className="level-dot"></span>
                  <span className="level-text">中等风险</span>
                </div>
              </div>
              <p className="risk-brief attention">血脂偏高 · 10年发病风险12%</p>
              <button 
                className="view-plan-btn"
                onClick={() => openRiskInfoPage('ascvd')}
              >
                查看逆转方案
              </button>
            </div>
            
            {/* 肿瘤预警 */}
            <div className="unified-risk-card safe">
              <div className="risk-card-header">
                <div className="risk-name-with-icon">
                  <span 
                    className="risk-name clickable-title"
                    onClick={() => openRiskInfoPage('tumor')}
                  >
                    肿瘤预警<button 
                      className="risk-explanation-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        openRiskInfoPage('tumor');
                      }}
                    >ⓘ</button>
                  </span>
                </div>
                <div className="risk-level-indicator safe">
                  <span className="level-dot"></span>
                  <span className="level-text">低风险</span>
                </div>
              </div>
              <p className="risk-brief safe">肺部小结节</p>
              <p className="risk-description-low">肺部小结节需定期观察，恶性概率较低，建议保持健康生活方式</p>
            </div>
          </div>
        </div>
      </div>
      
      {/* 2. 重点指标趋势 */}
      <div className="trends-section">
        <h4>重点指标趋势</h4>
        <div className="charts-container">
          <div className="chart-item">
            <div className="chart-header">
              <h5>血脂水平 (mmol/L)</h5>
              <div className="chart-target-info">
                <span className="target-label">目标值: &lt; 5.2</span>
              </div>
            </div>
            <TrendChart 
              data={bloodLipidData} 
              color="#FF6B6B" 
              unit=" mmol/L"
              thresholdValue={5.2}
              thresholdLabel="正常上限"
            />
          </div>
          <div className="chart-item">
            <div className="chart-header">
              <h5>肺结节大小 (mm)</h5>
              <div className="chart-target-info">
                <span className="target-label">监测值: &lt; 5.0</span>
              </div>
            </div>
            <TrendChart 
              data={lungNoduleData} 
              color="#4ECDC4" 
              unit=" mm"
              thresholdValue={5.0}
              thresholdLabel="关注线"
            />
          </div>
        </div>
        <div className="trends-actions">
          <button className="view-all-trends-external" onClick={handleViewAllTrends}>
            查看全部指标 <span className="arrow-icon">→</span>
          </button>
        </div>
      </div>

      {/* 3. 推荐处理方式 - 重新优化 */}
      <div className="recommendations-section">
        <h4>下一步最佳行动</h4>
        <div className="recommendation-items-clean">
          <div className="recommendation-item-enhanced">
            <div className="rec-content-enhanced">
              <div className="rec-main-content">
                <h5>血脂管理</h5>
                <p>控制血脂水平，降低心血管风险</p>
              </div>
              <span className="rec-reminder">添加提醒</span>
            </div>
          </div>
          
          <div className="recommendation-item-enhanced">
            <div className="rec-content-enhanced">
              <div className="rec-main-content">
                <h5>肺结节监测</h5>
                <p>定期CT检查，观察结节变化</p>
              </div>
              <span className="rec-reminder">添加提醒</span>
            </div>
          </div>
          
          <div className="recommendation-item-enhanced">
            <div className="rec-content-enhanced">
              <div className="rec-main-content">
                <h5>营养咨询</h5>
                <p>专业营养师指导，制定个性化饮食方案</p>
              </div>
              <button 
                className="call-doctor-btn"
                onClick={() => handleAppointment('营养咨询')}
              >
                呼叫医生
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* 5. 引用资料 */}
      <div className="references-section">
        <h4>引用资料</h4>
        <div className="reference-items">
          <div className="reference-item"><span>中国成人血脂异常防治指南(2023版)</span></div>
          <div className="reference-item"><span>肺结节诊治中国专家共识(2022版)</span></div>
        </div>
      </div>
    </div>
  );
};

export default HealthSummaryCardB; 